/**
 * DTO typy modulu Apex Ops. Vznikají ve fázi 1 jako závazný kontrakt pro budoucí BE
 * (fáze 7); každá další FE fáze je rozšiřuje. Peníze/jednotky jsou v mock vrstvě
 * `number`, BE bude pracovat s numeric(28,4)/numeric(28,8).
 */

/**
 * Fondy, u nichž jednotky počítáme MY (z Excelu). APEX ONE je VEN — dělá externí
 * NWD fond (jednotky/nacenění/fee/klienti mimo modul). Viz docs/16-apex-ops/09-fund-fee-model.md.
 */
export type StrategyKey = 'SEMI' | 'ENERGY' | 'GAMECHANGER' | 'BLOCKCHAIN' | 'TREASURY'

/**
 * Třída fondu: A = EB (Early Birds, nižší sazby), B = NORM (vyšší sazby). Určuje
 * per-klientskou sazbu fee (MF + SF), NE zvlášť cenu — jeden fond = jedna cena.
 * TREASURY má jen jednu třídu (mapuje se na A).
 */
export type FeeClass = 'A' | 'B'

/** Sazba fee (p.a.) per fond × třída. Editovatelné v UI (F6-A) — přehled místo hardcode. */
export interface FeeRate {
  fundKey: StrategyKey
  feeClass: FeeClass
  /** Management fee p.a. (např. 0.0147 = 1,47 %). */
  mgmtFeePa: number
  /** Success fee sazba (např. 0.15 = 15 %). */
  successFeeRate: number
  /** Výstupní poplatek při odkupu jednotek (např. 0.01 = 1 %); default 0. */
  exitFeeRate?: number
  /**
   * Základna provizí distributorů z MF p.a. (default 0.0147). MF je už v NAV od správce,
   * modul z něj počítá jen provize — jako Excel distributorů, nezávisle na třídě.
   */
  commissionBasePa?: number
}

export interface UpdateFeeRateRequest {
  fundKey: StrategyKey
  feeClass: FeeClass
  mgmtFeePa: number
  successFeeRate: number
  /** Výstupní poplatek (0 se posílá explicitně; prázdné = BE nechá stávající). */
  exitFeeRate?: number
  /** Základna provizí z MF p.a.; prázdné = BE nechá stávající. */
  commissionBasePa?: number
}

/**
 * Varianta apexové smlouvy → mapuje se na třídu fondu: EB → A, NORM → B, DOHODA → B.
 * (5 strategií × varianty × FO/PO šablony apex-generatoru.)
 */
export type ApexVariant = 'DOHODA' | 'NORM' | 'EB'

/** Třída fondu (fee) odvozená z varianty smlouvy. */
export function feeClassOfVariant(variant: ApexVariant): FeeClass {
  return variant === 'EB' ? 'A' : 'B'
}

export const APEX_VARIANTS: Array<{ key: ApexVariant; label: string }> = [
  { key: 'DOHODA', label: 'Dohoda o investici' },
  { key: 'NORM', label: 'Smlouva o správě (NORM)' },
  { key: 'EB', label: 'Smlouva o správě (EB)' },
]

/** Stavový automat vkladu (jádro flow — viz docs/16-apex-ops/02-plan.md). */
export type DepositStatus = 'WAITING_PAYMENT' | 'PAID' | 'IN_BATCH' | 'POSTED' | 'DELETED'

/** Stavový automat přepočtu (batch přidělení jednotek). */
export type BatchStatus = 'DRAFT' | 'CHECKED' | 'CONFIRMED' | 'REJECTED'

export type DocumentState = 'GENERATED' | 'SIGNED' | 'ARCHIVED' | 'CANCELLED'

export interface ClientSummary {
  id: string
  fullName: string
  /** RČ (normalizované, bez lomítka) — fyzická osoba. */
  personalNumber?: string
  /** IČO — právnická osoba. */
  companyId?: string
  email?: string
}

/**
 * Zapisovatelná pole klienta (formulář fáze 2). Zrcadlí pole entity `client`
 * v portilio-be; pole jen pro šablony generátorů (druh dokladu, občanství,
 * PO zástupce…) se do DB neukládají a doplní je BE ve fázi 7 z konfigurace/formuláře.
 */
export interface ClientInput {
  company: boolean
  companyName?: string
  /** IČO — právnická osoba. */
  companyId?: string
  titlePre?: string
  firstName?: string
  surname?: string
  titleAfter?: string
  /** RČ (normalizované, bez lomítka) — fyzická osoba. */
  personalNumber?: string
  /** ISO datum (yyyy-MM-dd). */
  birthDate?: string
  birthPlace?: string
  street?: string
  town?: string
  zipCode?: string
  contactStreet?: string
  contactTown?: string
  contactZip?: string
  identityCardNumber?: string
  /** ISO datum (yyyy-MM-dd). */
  identityCardValidity?: string
  identityCardIssuer?: string
  email?: string
  phone?: string
}

export interface Client extends ClientSummary, ClientInput {
  createdAt: string
  /** Přidělená obsluha (distributor) — klient z ní dědí sazbu management fee (F6-A). */
  distributorId?: string
}

export interface DocumentMeta {
  id: string
  clientId: string
  templateKey: string
  /** Lidský název dokumentu (pro seznamy). */
  label: string
  fileName: string
  strategy?: StrategyKey
  state: DocumentState
  createdAt: string
}

/** Obsah dokumentu ke stažení (content = base64). */
/** Formát vyúčtování fee ke stažení. */
export type StatementFormat = 'pdf' | 'xlsx'

export interface DocumentFile {
  fileName: string
  mimeType: string
  content: string
}

/** Šablona/dokument nabízený generátorem (zrcadlí GET /api/templates generátorů). */
export interface TemplateInfo {
  key: string
  label: string
  /** Statické PDF (Imperium) — negeneruje se, jen se přikládá do výstupu. */
  staticPdf: boolean
}

export interface Deposit {
  id: string
  clientId: string
  /** Denormalizované jméno pro fronty (řazení/hledání bez dotahování klientů). */
  clientName: string
  strategy: StrategyKey
  /** Třída fondu (A/B) odvozená z varianty smlouvy — určuje sazbu fee klienta. */
  feeClass: FeeClass
  /** Investovaná částka v CZK. */
  amount: number
  accountNumber?: string
  status: DepositStatus
  /** Čas vygenerování smluv (vstup do fronty plateb). */
  createdAt: string
  /** Čas potvrzení přijetí vkladu — řadí frontu přidělení jednotek. */
  paidAt?: string
  paidBy?: string
  /**
   * Skutečně přijatá částka (rozhodnutí F3-B) — může se lišit od sjednané `amount`;
   * jednotky ve fázi 4 se počítají z ní (fallback `amount`).
   */
  amountReceived?: number
  /** Při potvrzení platby odešel klientovi email (rozhodnutí F3-C). */
  emailSent?: boolean
  /** Ruční vklad mimo generátor — vznikl rovnou jako PAID (rozhodnutí F3-D). */
  manual?: boolean
  note?: string
  batchId?: string
  postedAt?: string
  /**
   * Při POTVRZENÍ přepočtu (fáze 5) odešel klientovi email „připsání jednotek"
   * — existující šablona + checkbox „Informovat klienta" (rozhodnutí F5-D).
   */
  unitsEmailSent?: boolean
  deletedAt?: string
  documentIds: string[]
}

/** Režim vstupního poplatku (klíče TYP_POPLATKU apex-generatoru). */
export type EntryFeeMode = 'INCREASE' | 'DEDUCT' | 'NONE'

/**
 * Generování Apex: jedna šablona (strategie + varianta), vytvoří dokument
 * a vklad WAITING_PAYMENT. `client` se posílá vždy; u stávajícího klienta
 * (clientId vyplněno) ho BE IGNORUJE a čte klienta z DB — údaje jsou ve
 * formuláři read-only (rozhodnutí F2-E, změněno 29.7.2026).
 * Založení klienta je plnohodnotné — žádný incomplete flag.
 */
export interface GenerateApexRequest {
  clientId?: string
  client: ClientInput
  strategy: StrategyKey
  variant: ApexVariant
  amount: number
  accountNumber: string
  entryFeePercent: number
  entryFeeMode: EntryFeeMode
  /** Novy klient: zalozit KC ucet + poslat prihlasovaci udaje / uvitaci email. */
  sendCredentials?: boolean
  sendWelcomeEmail?: boolean
  /** Nový klient: založit klienta a vklad v aplikaci (false = jen dokumenty ke stažení). */
  createClient?: boolean
  /** Nový klient: rovnou přiřadit distributora. */
  distributorId?: string
}

/** Generování Imperium: checklist dokumentů, bez fondu (žádný vklad). */
export interface GenerateImperiumRequest {
  clientId?: string
  client: ClientInput
  documentKeys: string[]
  sendCredentials?: boolean
  sendWelcomeEmail?: boolean
}

export interface GenerateResult {
  /** Chybí u Imperia s novým člověkem — tam se klient nezakládá. */
  client?: Client
  documents: DocumentMeta[]
  /** Jen Apex — nový vklad ve stavu WAITING_PAYMENT. */
  deposit?: Deposit
  /** null/undefined = nebylo pozadovano. */
  credentialsSent?: boolean | null
  welcomeEmailSent?: boolean | null
  clientEmailError?: string | null
  /** Soubor ke stažení; vyplněný jen když se nic neuložilo do archivu. */
  file?: DocumentFile
}

/**
 * Potvrzení přijetí platby (fáze 3). V BE spustí připsání na peněžní trh
 * (logika dnešního vkladu) + volitelný email klientovi.
 */
export interface ConfirmPaymentRequest {
  /** Skutečně přijatá částka — předvyplněná sjednanou, obsluha může upravit (F3-B). */
  amountReceived: number
  /** Poslat klientovi potvrzovací email (F3-C, default zapnuto). */
  sendEmail: boolean
}

/**
 * Ruční vklad stávajícího klienta mimo generátor (rozhodnutí F3-D) — peníze už
 * přišly, vklad vzniká rovnou ve stavu PAID a přeskočí frontu plateb.
 */
export interface ManualDepositRequest {
  clientId: string
  strategy: StrategyKey
  /**
   * Třída fondu (A = EB, B = NORM). U generovaného vkladu ji určí varianta
   * smlouvy; u ručního vkladu ji ZADÁVÁ OBSLUHA — bez ní by BE spadl na B
   * a klient by navždy platil vyšší MF i SF.
   */
  feeClass: FeeClass
  amount: number
  accountNumber?: string
  note?: string
}

/**
 * Platební údaje vkladu pro QR dialog ve frontě plateb (rozhodnutí F2-C).
 * SPD řetězec skládá BE (zná sběrný účet; pozor AM max 10 znaků — fix KB
 * z apex-generatoru), FE z něj renderuje QR kód.
 */
export interface PaymentInfo {
  /** Sběrný účet ve formátu [predčíslí-]číslo/kód. */
  account: string
  iban: string
  amount: number
  currency: 'CZK'
  variableSymbol: string
  message: string
  /** SPD řetězec pro QR platbu. */
  spd: string
}

/** Jedna položka NAV (banka, broker, … — seznam určí Vašek, otázka F4-B). */
export interface NavItem {
  label: string
  amount: number
}

export interface Valuation {
  id: string
  fundKey: StrategyKey
  /** Datum ocenění. NAV je vždy BEZ nových vkladů. */
  date: string
  items: NavItem[]
  navTotal: number
  /** Odvozené: navTotal / totalUnitsAt. */
  unitPrice: number
  /** Snapshot celkových jednotek v okamžiku ocenění (audit). */
  totalUnitsAt: number
  confirmedBy: string
  createdAt: string
  /** Kdy se podle ocenění aktualizovala cena instrumentu v jádru; null = ještě ne. */
  instrumentPriceAt?: string | null
  /** Cena instrumentu v jádru k datu ocenění (třída A); null = žádná. */
  instrumentPrice?: number | null
}

export interface BatchParticipant {
  depositId: string
  clientId: string
  clientName: string
  /** Třída fondu klienta (z varianty smlouvy) — nese se do pozice pro sazby fee. */
  feeClass: FeeClass
  /** Vklad použitý pro výpočet jednotek = amountReceived ?? amount (F3-B). */
  amount: number
  /** Přidělené jednotky = amount / unitPrice (pokud není override). */
  units: number
  priceOverride?: number
  unitsOverride?: number
}

export interface AutoTestResult {
  name: string
  passed: boolean
  detail?: string
}

/**
 * AI výsledek per subjekt (u batche = klient). Shodné se `FeeAiResult` i s BE
 * `AiClientResultDto` — `id` je id subjektu, `name` jeho jméno (BE ho posílá
 * denormalizované, aby šel řádek zobrazit i bez dohledání v účastnících).
 */
export interface AiClientResult {
  id: string
  name: string
  status: 'OK' | 'ERROR'
  comment: string
}

export interface BatchCheck {
  autoTests: AutoTestResult[]
  aiResults: AiClientResult[]
  checkedAt: string
}

/** Přepočet = jedno přidělení jednotek. Každý přepočet zůstává dohledatelný (audit trail). */
export interface UnitBatch {
  id: string
  fundKey: StrategyKey
  /** Datum přidělení (vybírá CEO). */
  assignmentDate: string
  valuationId: string
  navTotal: number
  unitPrice: number
  totalUnitsBefore: number
  totalUnitsAfter: number
  participants: BatchParticipant[]
  status: BatchStatus
  check?: BatchCheck
  createdBy: string
  createdAt: string
  decidedAt?: string
  decidedBy?: string
}

/**
 * Založení ocenění (fáze 4, jen CEO). NAV = Σ položek (volné řádky, F4-B),
 * vždy BEZ nových vkladů. Cenu jednotky a snapshot total_units odvozuje server;
 * BE navíc při uložení aktualizuje cenu instrumentu v core (F4-C, řeší F7).
 */
export interface CreateValuationRequest {
  fundKey: StrategyKey
  /** Datum ocenění (yyyy-MM-dd). Nesmí být starší než poslední ocenění fondu. */
  date: string
  items: NavItem[]
}

/** Účastník requestu na přepočet; overrides = ruční výjimka (precedent Šenkeřík, F4-E). */
export interface CreateBatchParticipant {
  depositId: string
  priceOverride?: number
  unitsOverride?: number
}

/**
 * Vytvoření přepočtu (DRAFT, fáze 4, jen CEO). Jednotky počítá server
 * z `amountReceived ?? amount` vkladu a ceny ocenění; vklady PAID → IN_BATCH.
 * `total_units` fondu se navyšuje až POTVRZENÍM batche (fáze 5).
 */
export interface CreateBatchRequest {
  fundKey: StrategyKey
  valuationId: string
  /** Datum přidělení (vybírá CEO, yyyy-MM-dd). */
  assignmentDate: string
  participants: CreateBatchParticipant[]
}

/**
 * Potvrzení přepočtu (fáze 5, jen CEO). BE před zápisem provede plný pg_dump
 * (rozhodnutí F5-C), navýší `total_units` o Σ jednotek KUMULATIVNĚ, vklady
 * IN_BATCH → POSTED a dle `sendEmail` odešle klientům email o připsání jednotek
 * (existující šablona, rozhodnutí F5-D). Idempotence + status-guard: batch smí
 * přejít jen z DRAFT/CHECKED, nikdy 2×.
 */
export interface ConfirmBatchRequest {
  /** Informovat klienta emailem o připsání jednotek (F5-D, default zapnuto). */
  sendEmail: boolean
}

export interface Fund {
  key: StrategyKey
  name: string
  /** KUMULATIVNÍ celkový počet jednotek (nikdy klouzavé okno — SEMI bug). Jedna cena per fond. */
  totalUnits: number
  /** Podíl distributora ze success fee (distribuce, např. 0.2 = 20 %). Sdílené per fond. */
  distributionRate: number
  /** Fond s jedinou třídou (TREASURY) — nemá A/B, sazba jen třída A. */
  singleClass: boolean
  lastValuation?: { date: string; unitPrice: number; navTotal: number }
}

/**
 * Ruční nastavení celkového počtu jednotek fondu (jen CEO).
 *
 * `totalUnits` je ZÁMĚRNĚ string, ne number: total_units je numeric(28,8) a u desítek
 * milionů jednotek s osmi desetinnými místy už double zaokrouhluje. Tohle jediné číslo
 * je jmenovatel ceny jednotky, takže se posílá přesně tak, jak ho obsluha napsala.
 */
export interface SetTotalUnitsRequest {
  /** Nový celkový počet jednotek, tečka jako oddělovač, bez mezer (např. „186000000.12345678"). */
  totalUnits: string
  /** Povinné odůvodnění opravy — jde do auditu (u velké změny ho vyžaduje i BE). */
  note: string
}

/** Řádek auditu ruční opravy celkových jednotek fondu (BE vrací nejnovější první). */
export interface TotalUnitsChange {
  id: string
  fundKey: StrategyKey
  /** Stav před opravou a po ní (numeric(28,8)). */
  totalUnitsBefore: number
  totalUnitsAfter: number
  /** Důvod, který obsluha zadala. */
  note: string
  /** Kdo opravu uložil (z tokenu) a kdy (ISO datum a čas). */
  changedBy: string
  changedAt: string
}

/**
 * Jeden řádek souhrnu načtení držby z core: fond × třída. `clients` je počet klientů
 * s kladnou pozicí v TÉTO kombinaci — klient držící víc fondů se počítá v každém řádku,
 * proto součet sloupce bývá vyšší než `PositionImportResult.clients`.
 */
export interface PositionImportFundLine {
  fundKey: StrategyKey
  feeClass: FeeClass
  clients: number
  /** Σ jednotek (numeric(28,8)) a Σ investované částky (numeric(28,4)) za řádek. */
  units: number
  invested: number
}

/**
 * Výsledek náhledu i ostrého načtení držby klientů z core (stejný tvar pro obojí).
 *
 * Držbu (kdo, kolik jednotek, v jakém fondu a třídě, kolik vložil) drží modul ve své
 * tabulce a počítá z ní management fee, success fee i limity odkupů. Na produkci ale
 * začíná prázdná, zatímco reálné pozice existují v core z běžných nákupů — tohle je
 * jejich jednorázové (a v případě rozjetí opakovatelné) překopírování.
 */
export interface PositionImportResult {
  /** true = jen náhled, do modulu se NIC nezapsalo. */
  preview: boolean
  /** Kdy a kdo načetl — vyplněné jen u ostrého načtení (u náhledu null). */
  importedAt: string | null
  importedBy: string | null
  /** Kolik řádků držby by vzniklo (resp. vzniklo) a kolika různých klientů se týkají. */
  rows: number
  clients: number
  funds: PositionImportFundLine[]
  /** Hotové české věty z BE (přeskočené pozice apod.) — vypisují se tak, jak přijdou. */
  warnings: string[]
}

/** Přebytek jednotek fondu = celek − Σ držeb (převádí se na účet „opravná položka“). */
export interface FundSurplus {
  fundKey: StrategyKey
  totalUnits: number
  positionsUnits: number
  surplus: number
  /** Cena posledního ocenění; null = fond nemá ocenění. */
  price: number | null
  correctionClientId: string | null
}

/** Souhrn importu tranší za fond × třídu (jen NOVÉ tranše). */
export interface TrancheImportFundLine {
  fundKey: StrategyKey
  feeClass: FeeClass
  tranches: number
  units: number
  amount: number
  firstDate: string | null
  lastDate: string | null
}

/** Minulé zúčtování SF odvozené z prodejů na konci roku (high-water mark). */
export interface TrancheImportSettlement {
  fundKey: StrategyKey
  year: number
  yearEndPrice: number
  feeUnits: number
  fee: number
  clients: number
  /** true = běh v modulu už existuje, import ho znovu nezakládá. */
  exists: boolean
}

/**
 * Náhled i ostrý import historie vkladů (tranší) z core. Výkonnostní odměna se počítá
 * po tranších a provize z MF až od měsíce po vkladu — import pozic přenese jen součty.
 */
export interface TrancheImportResult {
  preview: boolean
  importedAt: string | null
  importedBy: string | null
  tranches: number
  /** Obchody, které modul už zná (připsal je sám nebo byly importované dřív). */
  alreadyKnown: number
  clients: number
  funds: TrancheImportFundLine[]
  settlements: TrancheImportSettlement[]
  warnings: string[]
}

// ---- Fee (fáze 6) ----

/** AI kontrola fee — subjekt = distributor (MF) nebo klient (SF). */
export interface FeeAiResult {
  id: string
  name: string
  status: 'OK' | 'ERROR'
  comment: string
}

/** Kontrola fee běhu: automatické testy (invarianty) + AI komentář + čas. */
export interface FeeCheck {
  autoTests: AutoTestResult[]
  aiResults: FeeAiResult[]
  checkedAt: string
}

/**
 * Distributor (obsluha). Klient je k němu přiřazen přes `Client.distributorId`
 * (v BE F7 přes roli obsluhy). Sazba management fee NENÍ na distributorovi —
 * je per fond×třída (`FeeRate`); distributor slouží k evidenci a reportu MF
 * (kdo přivedl klienty) + k distribuci success fee.
 */
export interface Distributor {
  id: string
  name: string
  /** Jmenná role obsluhy v core; null = interní (Apex). */
  roleId?: string | null
  roleName?: string | null
  email?: string | null
  active?: boolean
  /** Posílat distributorovi skrytou kopii e-mailů jeho klientům. */
  notifyClientEvents?: boolean
  /** Počet klientů v obsluze (dle client_role). */
  clientCount?: number | null
  /** Login distributorského účtu; null = bez přístupu do aplikace. */
  login?: string | null
  /** Dočasné heslo — jen v odpovědi na založení účtu / nové heslo, zobrazí se jednou. */
  generatedPassword?: string | null
  /** Nasmlouvaný podíl ze strženého MF jeho klientů (0.5 = 50 %). */
  mfCommissionRate?: number | null
  /** Nasmlouvaný podíl ze strženého SF; null = sazba fondu. */
  sfCommissionRate?: number | null
  /** FIXED = pevné podíly výše, VOLUME = objemová pásma (smlouva od 6/2026). */
  commissionMode?: CommissionMode
  /** Pásma pro VOLUME (bez vlastních = pásma ze smlouvy). */
  commissionTiers?: CommissionTier[] | null
  /** VOLUME: vklady − výběry jeho klientů k dnešku a podíly z aktuálního pásma. */
  volume?: number | null
  currentMfRate?: number | null
  currentSfRate?: number | null
}

export type CommissionMode = 'FIXED' | 'VOLUME'

/** Objemové pásmo: od objemu (Kč; hranice ještě patří nižšímu pásmu) → podíl z MF a SF (0.3 = 30 %). */
export interface CommissionTier {
  from: number
  mf: number
  sf: number
}

export interface CreateDistributorRequest {
  name: string
  email?: string
  firstName?: string
  lastName?: string
  /** Vytvořit přihlašovací účet (Keycloak, role DISTRIBUTOR); údaje se zobrazí obsluze, nic se neposílá. */
  createLogin?: boolean
  notifyClientEvents?: boolean
  /** Podíl ze strženého MF (0.5 = 50 %). */
  mfCommissionRate?: number | null
  /** Podíl ze strženého SF; prázdné = sazba fondu. */
  sfCommissionRate?: number | null
}

export interface UpdateDistributorRequest {
  name?: string
  email?: string
  active?: boolean
  notifyClientEvents?: boolean
  mfCommissionRate?: number | null
  sfCommissionRate?: number | null
  commissionMode?: CommissionMode
  /** Prázdné pole = pásma ze smlouvy. */
  commissionTiers?: CommissionTier[]
}

/** Klient v obsluze distributora. */
export interface DistributorClient {
  clientId: string
  fullName: string
  personalNumber?: string | null
  companyId?: string | null
  email?: string | null
}

/** Řádek měsíčního management fee = 1 klient. MF sazba per třída; strhává se z NAV fondu (nepřímo). */
export interface MgmtFeeLine {
  /** Podíl distributora ze strženého fee (0.5 = 50 %). */
  commissionRate?: number | null
  /** Provize distributora = fee × podíl; zbytek zůstává fondu. */
  commission?: number | null
  clientId: string
  clientName: string
  distributorId: string
  distributorName: string
  /** Hodnota drženého majetku klienta (Σ fondy: jednotky × cena posledního ocenění). */
  holdingValue: number
  /** Efektivní sazba (vážený průměr p.a. přes fondy/třídy klienta) — pro přehled. */
  ratePa: number
  /** Měsíční fee = Σ pozic (hodnota × sazba_třídy / 12) (HALF_UP, 4 des.). */
  fee: number
}

/** Součet management fee za distributora (report per distributor — F6: evidence per distributor). */
export interface MgmtFeeDistributorTotal {
  distributorId: string
  distributorName: string
  clientCount: number
  totalHoldingValue: number
  totalFee: number
  /** Provize distributora = Σ (fee klienta × jeho podíl); zbytek zůstává fondu. */
  totalCommission?: number | null
}

/**
 * Snížení NAV fondu měsíčním MF. Od 13. 10. 2026 se už nezakládá (MF je v NAV od správce),
 * u starých běhů může ještě přijít.
 */
export interface MgmtFeeFundReduction {
  fundKey: StrategyKey
  navBefore: number
  navAfter: number
  mfAmount: number
  unitPriceAfter: number
}

/**
 * Měsíční běh management fee = podklad pro provize distributorů. UNIQUE per měsíc.
 * MF je už v NAV od správce, běh cenu jednotky ani jednotky klientů nemění.
 * Řádek klienta = jednotky přidělené před měsícem × cena ke konci měsíce × základna / 12.
 */
export interface MgmtFeeRun {
  id: string
  /** Měsíc yyyy-MM. */
  period: string
  lines: MgmtFeeLine[]
  byDistributor: MgmtFeeDistributorTotal[]
  /** Snížení NAV per fond (nepřímý dopad — cena jednotky klesla). */
  fundReductions: MgmtFeeFundReduction[]
  totalFee: number
  /** Σ provizí distributorů; fond si nechá totalFee − totalCommission. */
  totalCommission?: number | null
  /** Automatické testy + AI kontrola (per distributor). */
  check?: FeeCheck
  createdAt: string
  createdBy: string
}

export interface RunMgmtFeeRequest {
  /** Měsíc yyyy-MM. */
  period: string
}

export type SuccessFeeStatus = 'DRAFT' | 'CONFIRMED'

/** Jedna tranše = jeden vklad (řádek Excel reportu). Benchmark je gate: nesplněn => fee 0. */
export interface SuccessFeeTranche {
  depositId: string | null
  /** Datum investice (assignment date batche). */
  date: string | null
  invested: number
  /** Cena jednotky v den nákupu. */
  buyPrice: number | null
  units: number
  value: number
  /** Výchozí hodnota pro zisk: max(investováno, jednotky × HWM cena z minulých zúčtování). */
  baselineValue: number
  profit: number
  /** Zhodnocení p.a. (jednoduché úročení, Actual/360 dle smlouvy). */
  returnPa: number
  benchmarkMet: boolean
  fee: number
}

/** Řádek success fee = 1 držitel jednotek fondu (Σ tranší, benchmark gate per tranše). */
export interface SuccessFeeLine {
  clientId: string
  clientName: string
  /** Třída držitele (A/B) — určuje sazbu success fee. */
  feeClass: FeeClass
  /** Success fee sazba držitele dle třídy (15 % / 20 %). */
  rate: number
  units: number
  invested: number
  /** units × cena konec roku. */
  value: number
  /** max(0, value − invested). */
  profit: number
  /** profit × successFeeRate. */
  fee: number
  /** fee / cena konec roku — počet stržených jednotek. */
  feeUnits: number
  /** fee × podíl distributora (sf_commission_rate, fallback sazba fondu). */
  distribution: number
  distributorId?: string | null
  /** Použitý podíl distributora ze strženého fee. */
  commissionRate?: number | null
  /** Rozpad per vklad — zdroj fee i benchmarku. */
  tranches?: SuccessFeeTranche[]
}

/**
 * Roční success fee fondu (fáze 6). Vzniká DRAFT (přehled) → POTVRZENÍ = stržení
 * jednotek držitelům (BE Deal SELL; mock sníží total_units o Σ fee_units) + email.
 * UNIQUE per fond+rok. Spouští/potvrzuje backoffice i CEO (F6-D).
 */
export interface SuccessFeeRun {
  id: string
  fundKey: StrategyKey
  year: number
  /** Cena jednotky ke konci roku, ze které se počítá zisk a stržení. */
  yearEndPrice: number
  /** Benchmark p.a. (gate), default 7 %. */
  benchmarkPa?: number
  status: SuccessFeeStatus
  lines: SuccessFeeLine[]
  totalFee: number
  totalFeeUnits: number
  totalDistribution: number
  /** Automatické testy + AI kontrola (per klient). */
  check?: FeeCheck
  createdAt: string
  createdBy: string
  decidedAt?: string
  decidedBy?: string
}

export interface CreateSuccessFeeRequest {
  fundKey: StrategyKey
  year: number
  /** Cena jednotky ke konci roku; default = poslední ocenění fondu (F6-C ruční zaklik CEO). */
  yearEndPrice?: number
}

export interface ConfirmSuccessFeeRequest {
  /** Poslat držitelům email s přehledem stržení (F6-D). */
  sendEmail: boolean
}

// ---- Odkupy jednotek (fáze 5b) ----

/**
 * Stavový automat odkupu. NEW = nová žádost (čeká na podepsaný originál),
 * QUEUED = ve frontě na Den ocenění, REQUESTED = oceněno (nic v core),
 * CONFIRMED = zapsáno do core (prodej jednotek + srážka SF), PAID_OUT = peníze
 * odešly z banky, CANCELLED = storno nepotvrzené žádosti.
 */
export type RedemptionStatus = 'NEW' | 'QUEUED' | 'REQUESTED' | 'CONFIRMED' | 'PAID_OUT' | 'CANCELLED'

/**
 * Odkup jednotek = klient prodává jednotky fondu. Zrcadlo vkladu: cenu určuje
 * ocenění, dle smlouvy se z odkupované části zúčtuje výkonnostní odměna (stejná
 * mechanika jako roční běh — tranše, high-water mark, benchmark 7 % p.a.) a
 * strhne se výstupní poplatek. Odměna i poplatek se strhávají v KČ z výplaty,
 * ne v jednotkách — klient prodá všechny odkupované jednotky a dostane netto.
 */
export interface Redemption {
  id: string
  clientId: string
  /** Denormalizované jméno pro fronty. */
  clientName: string
  fundKey: StrategyKey
  /** Třída držby (A/B) — určuje sazbu success fee. */
  feeClass: FeeClass
  /** Zadaný počet jednotek (když se odkup zadával jednotkami). */
  requestedUnits?: number
  /** Zadaná částka v Kč (když se odkup zadával částkou). */
  requestedAmount?: number

  valuationId?: string
  /** Datum ocenění, jehož cenou se odkup ocenil. */
  valuationDate?: string
  unitPrice: number
  /** Skutečně odkupované jednotky (dopočtené z částky, nebo zadané). */
  units: number
  /** units × cena jednotky. */
  grossAmount: number

  /** Sazba success fee třídy (A/EB 15 %, B/NORM 20 %). */
  sfRate: number
  /** Výkonnostní odměna z odkupované části (Σ fee tranší). */
  sfFee: number
  /**
   * Jednotky odměny: přejdou na účet „success fee“, z fondu se odkupuje jen units − sfUnits.
   * Klient dostane stejnou čistou výplatu jako dřív (13. 10. 2026).
   */
  sfUnits?: number
  /** Benchmark p.a. (gate), default 7 % — Actual/360. */
  benchmarkPa?: number
  /** Rozpad odměny per vklad — stejný tvar jako roční success fee. */
  tranches?: SuccessFeeTranche[]

  distributorId?: string | null
  distributorName?: string | null
  /** Podíl distributora ze strženého SF (fallback sazba fondu). */
  sfCommissionRate?: number | null
  /** Provize distributora = sfFee × podíl. */
  sfCommission?: number | null

  /** Sazba výstupního poplatku (0.01 = 1 %); default 0. */
  exitFeeRate: number
  /** grossAmount × exitFeeRate. */
  exitFee: number
  /** grossAmount − sfFee − exitFee = co se klientovi opravdu pošle. */
  netAmount: number

  /** Držba klienta ve fondu (u žádosti PŘED odkupem, po potvrzení už snížená). */
  holdingUnits?: number

  /** Den doručení žádosti (yyyy-MM-dd) — žádost ve frontě. */
  receivedDate?: string | null
  /** Smluvní Den ocenění (nejdřív 14 dní po doručení). */
  valuationDay?: string | null
  redeemAll?: boolean
  byManager?: boolean
  /** Proč žádost ve frontě zatím nešla ocenit. */
  queueError?: string | null
  /** Splatnost výplaty (30 / 90 dní od Dne ocenění). */
  payoutDue?: string | null

  accountNumber?: string
  status: RedemptionStatus
  note?: string
  /** Automatické testy + AI kontrola (per klient) — stejný tvar jako u fee běhů. */
  check?: FeeCheck

  createdAt: string
  createdBy?: string
  decidedAt?: string
  decidedBy?: string
  paidAt?: string
  paidBy?: string
  cancelledAt?: string
  emailSent?: boolean
}

/**
 * Žádost o odkup (jen backoffice). Zadává se BUĎ počet jednotek, NEBO částka —
 * druhé dopočte server cenou ocenění. `valuationId` prázdné = poslední ruční
 * ocenění fondu, `exitFeeRate` prázdné = sazba fondu pro třídu klienta (default 0).
 */
export interface CreateRedemptionRequest {
  clientId: string
  fundKey: StrategyKey
  units?: number
  amount?: number
  valuationId?: string
  accountNumber?: string
  exitFeeRate?: number
  note?: string
  /**
   * Třída poplatku, ze které se odkupuje. Prázdné = odvodí se z pozice klienta,
   * což stačí skoro vždy. Vyplnit je nutné jen u klientů, kteří drží v jednom
   * fondu obě třídy — každá má vlastní sazby i tranše, sečíst je nelze.
   */
  feeClass?: FeeClass
  /** true = odkoupit celou držbu třídy přesně (server vezme držbu z modulu); units ani amount se nezadávají. */
  all?: boolean
  /** Den doručení žádosti; vyplněný = žádost jde do fronty k Dni ocenění (čl. 8.1 smlouvy). */
  receivedDate?: string
  /** Klientem určený pozdější Den ocenění (volitelné). */
  valuationDay?: string
}

/**
 * Potvrzení odkupu (jen CEO): zápis do core (prodej jednotek, jednotky odměny na účet
 * „success fee“, výplata), snížení celkových jednotek fondu o odkoupenou část. BE znovu prověří invarianty proti aktuální držbě.
 */
export interface ConfirmRedemptionRequest {
  /** Poslat klientovi rozpis (hrubá hodnota, odměna, poplatek, čistá výplata). */
  sendEmail: boolean
}

/**
 * Přepínače automatických akcí modulu (per tenant; GET/PUT /api/funds/config).
 * Všechny mají default true = dnešní chování. Vypínají se kvůli pilotnímu provozu
 * na produkci: obsluha si zapíná kroky, které už zvládá, a hlavně drží v ruce
 * odesílání e-mailů — ty chodí reálným klientům.
 */
export interface FundConfig {
  /** Per-tenant zapnutí celého modulu (gating v OpsRoot). V UI se nepřepíná. */
  enabled: boolean
  /** Hlavní vypínač: false = z modulu neodejde klientovi žádný e-mail. */
  emailsEnabled: boolean
  /** Souhrnný e-mail distributorovi po přidělení jednotek jeho klientům. */
  distributorNotifications: boolean
  /** Založení účtu do WM a odeslání přihlašovacích údajů novému klientovi. */
  createLogins: boolean
  /** Založení klienta (a u Apexu i vkladu) při generování smluv. */
  createClients: boolean
  /** Interní účet Apexu „opravná položka“ (přebytek jednotek z historie); null = nenastaveno. */
  correctionClientId: string | null
  /** Interní účet Apexu „success fee“ (sem přecházejí stržené jednotky); null = nenastaveno. */
  successFeeClientId: string | null
}

/** Změna přepínačů (jen backoffice). Vynechané pole = ponechat beze změny. */
export interface UpdateFundConfigRequest {
  emailsEnabled?: boolean
  distributorNotifications?: boolean
  createLogins?: boolean
  createClients?: boolean
  /** Id klienta; '0' = odnastavit. */
  correctionClientId?: string
  successFeeClientId?: string
}
